import { useState, useEffect, useCallback } from 'react';
import { useCompanyFilter } from '../contexts/CompanyFilterContext';
import { store } from '../../../redux/store';
import { veevaApi } from '../../../redux/RTK/veevaReconcileSlice';
import { getReconActionAudit } from '../apis/reconAuditApi';

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 700,
  color: '#0039A6', textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: '2px solid #0098DB', backgroundColor: '#f8f9fa', whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '9px 12px', fontSize: '13px', color: '#333',
  borderBottom: '1px solid #eee', verticalAlign: 'top',
};

function formatDate(value) {
  if (value == null) return '—';
  if (typeof value === 'string') return value.slice(0, 19).replace('T', ' ');
  if (Array.isArray(value) && value.length >= 3) {
    const [y, m, d] = value;
    return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  }
  return '—';
}

export function AuditLog() {
  const { selectedCompany } = useCompanyFilter();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [startDate, setStartDate] = useState('2020-01-01');
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });
  const [orderNumber, setOrderNumber] = useState('');
  const [itemNumber, setItemNumber] = useState('');

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = { limit: 500 };
    if (startDate?.trim()) params.from_date = startDate.trim().slice(0, 10);
    if (endDate?.trim()) params.to_date = endDate.trim().slice(0, 10);
    if (orderNumber.trim()) params.order_num = orderNumber.trim();
    if (itemNumber.trim()) params.item_number = itemNumber.trim();

    const promise = store.dispatch(veevaApi.endpoints.getReconAudit.initiate(params));
    try {
      const data = await promise.unwrap();
      let all = Array.isArray(data) ? data : [];
      if (selectedCompany?.trim()) {
        const comp = selectedCompany.trim().toLowerCase();
        all = all.filter((r) => (r.company ?? '').toLowerCase().includes(comp));
      }
      setTotal(all.length);
      const start = (page - 1) * pageSize;
      setRows(all.slice(start, start + pageSize));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load audit records');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
      promise.unsubscribe();
    }
  }, [page, pageSize, selectedCompany, orderNumber, itemNumber, startDate, endDate]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  // Call /api/recon-action-audit — kept for future use, no display yet
  useEffect(() => {
    getReconActionAudit().catch(() => {});
  }, []);

  return (
    <div>
      {/* Search Filters */}
      <article className="card">
        <header>
          <i className="fa fa-search"></i>
          <label>Search Filters</label>
        </header>
        <div className="body">
          <div className="group">
            <div className="span3">
              <div className="field">
                <label>Start Date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
            </div>
            <div className="span3">
              <div className="field">
                <label>End Date</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <div className="span3">
              <div className="field">
                <label>Order Number</label>
                <input type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Enter order number" />
              </div>
            </div>
            <div className="span3">
              <div className="field">
                <label>Item Number</label>
                <input type="text" value={itemNumber} onChange={(e) => setItemNumber(e.target.value)} placeholder="Enter item number" />
              </div>
            </div>
          </div>
          <div className="group">
            <div className="span12">
              <button className="btn-blue right" onClick={() => { setPage(1); }}>
                <i className="fa fa-search"></i> Search
              </button>
            </div>
          </div>
        </div>
      </article>

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginBottom: '15px' }}>
          {error}
        </div>
      )}

      {/* Results Table */}
      <article className="card">
        <header>
          <i className="fa fa-history"></i>
          <label>Recon Audit ({total})</label>
        </header>
        <div className="body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Recon ID', 'Company', 'Order #', 'Sub Order', 'Item #', 'Tracking #',
                    'HIB Delivery Date', 'Veeva Delivery Date',
                    'HIB AOC Status', 'Veeva AOC Status', 'Recon Status',
                    'Veeva Ord Identifier', 'Veeva AOC Identifier',
                    'Comments', 'PCR #', 'Updated By', 'Updated On', 'Active'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={18} style={{ ...tdStyle, textAlign: 'center', padding: '40px', color: '#999' }}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={18} style={{ ...tdStyle, textAlign: 'center', padding: '40px', color: '#999' }}>No records found.</td></tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.reconId} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f7ff'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}>
                      <td style={tdStyle}>{row.reconId ?? '—'}</td>
                      <td style={tdStyle}>{row.company ?? '—'}</td>
                      <td style={tdStyle}>{row.orderNum ?? '—'}</td>
                      <td style={tdStyle}>{row.subOrderNum ?? '—'}</td>
                      <td style={tdStyle}>{row.itemNumber ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.trackingNum ?? ''}>{row.trackingNum ?? '—'}</td>
                      <td style={tdStyle}>{formatDate(row.hibbertDeliveryDate)}</td>
                      <td style={tdStyle}>{formatDate(row.veevaDeliveryDate)}</td>
                      <td style={tdStyle}>{row.hibAocStatus ?? '—'}</td>
                      <td style={tdStyle}>{row.veevaAocStatus ?? '—'}</td>
                      <td style={tdStyle}>{row.reconStatus ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.veevaOrdIdentifier ?? ''}>{row.veevaOrdIdentifier ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.veevaAocIdentifer ?? ''}>{row.veevaAocIdentifer ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.comments ?? ''}>{row.comments ?? '—'}</td>
                      <td style={tdStyle}>{row.pcrNum ?? '—'}</td>
                      <td style={tdStyle}>{row.updatedBy === '-1' ? '—' : (row.updatedBy ?? '—')}</td>
                      <td style={{ ...tdStyle, color: '#666' }}>{formatDate(row.updatedOn)}</td>
                      <td style={tdStyle}>{row.activeFlag === 1 ? 'Yes' : 'No'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && total > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #eee', fontSize: '13px', color: '#666' }}>
              <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-blue" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ padding: '4px 14px', height: '30px', fontSize: '12px', opacity: page <= 1 ? 0.5 : 1 }}>Previous</button>
                <button className="btn-blue" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} style={{ padding: '4px 14px', height: '30px', fontSize: '12px', opacity: page * pageSize >= total ? 0.5 : 1 }}>Next</button>
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
